import * as React from 'react';

import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { Button } from 'reactstrap';

import { ApplicationState } from '../../Store';
import { 
    GetDataCollectionLimits, 
} from '../../Store/Tenant';

import {
    GetPortalText,
} from '../../Store/PortalAssets';
import {
    GetPortalBusyState,
    actionCreators as UIStateActions,
} from '../../Store/UIState';
import {
    actionCreators as AppSettingsActions,
} from '../../Store/AppSettings';
import {
    actionCreators as ImportFinancialsActions,
} from '../../Store/ImportFinancials';

import { 
    PortalSafeDataCollectionLimitResponse,
    PortalTextDataCollectionLimitDetails,
    PortalTextCommon,
} from '../../Models/Api/strongbox.financialportal';
import {
    StringFormat,
} from '../../Utils/StringUtils';

import {
    Tooltip
} from '@mui/material';

type InjectedReduxState = {
    commonText?: PortalTextCommon;
    collectionLimitText?: PortalTextDataCollectionLimitDetails;
    dataCollectionLimits?: PortalSafeDataCollectionLimitResponse;
    portalBusy: boolean;
};

type InjectedActionCreators = typeof ImportFinancialsActions & typeof UIStateActions & typeof AppSettingsActions;

type LenderHomeProps = {
    workspaceId: string;
    workspaceDisplayName: string;
    buttonStyle?: React.CSSProperties;
};

type Props = LenderHomeProps & InjectedActionCreators & InjectedReduxState;

const NewDataCollectionButtonComponent: React.FC<Props> = (props): React.ReactElement => {
    const {
        buttonStyle,
        commonText,
        collectionLimitText,
        dataCollectionLimits,
        portalBusy,
        workspaceId,
        workspaceDisplayName,
        SetImportCustomerFinancialsActive,
        SetShowDataCollectionLimitsDetails,
    } = props;

    const tooltipText = React.useMemo(() => {
        if (!dataCollectionLimits || !dataCollectionLimits.hasLimit) {
            return commonText?.newDataCollectionTooltip || '';
        }
        const xOfYUsed = StringFormat(
            collectionLimitText?.templateDataXOfYDataCollectionsRemaining || '', 
            dataCollectionLimits.countRemaining,
            dataCollectionLimits.limit
        );
        return `${commonText?.newDataCollectionTooltip || ''} - ${xOfYUsed}`;

        // These are the only dependencies I really want.
        // eslint-disable-next-line react-hooks/exhaustive-deps 
    }, [dataCollectionLimits, collectionLimitText, commonText]);

    const dcLimitExceeded = React.useMemo(() => {
        return !!dataCollectionLimits && dataCollectionLimits.hasLimit && (dataCollectionLimits.countRemaining! <= 0);
    }, [dataCollectionLimits]);

    return (
        <Tooltip title={tooltipText}>
            <span>
                <Button
                    className={'small text'}
                    style={buttonStyle}
                    onClick={() => {
                        if (dcLimitExceeded) {
                            SetShowDataCollectionLimitsDetails(true);
                        } else {
                            SetImportCustomerFinancialsActive(
                                true,
                                {
                                    workspaceId: workspaceId,
                                    workspaceName: workspaceDisplayName,
                                }
                            );
                        }
                    }}
                    disabled={portalBusy}
                    color="primary"
                >
                    {commonText?.newDataCollection || ''}
                </Button>
            </span>
        </Tooltip>
    )
}

export const NewDataCollectionButton = connect<InjectedReduxState, InjectedActionCreators, LenderHomeProps, ApplicationState>(
    (appState: ApplicationState) => {
        const result: InjectedReduxState = {
            commonText: GetPortalText<PortalTextCommon>(appState, 'commonText'),
            collectionLimitText: GetPortalText<PortalTextDataCollectionLimitDetails>(appState, 'dataCollectionLimitDetails'),
            dataCollectionLimits: GetDataCollectionLimits(appState),
            portalBusy: GetPortalBusyState(appState),
        };

        return result;
    },
    dispatch => bindActionCreators(
        {
            ...ImportFinancialsActions,
            ...UIStateActions,
            ...AppSettingsActions,
        },
        dispatch
    )
)(NewDataCollectionButtonComponent);
